import { useUiStore } from "@/store/uiStore";
import { getApiErrorMessage, sanitizeApiErrorMessage } from "@/utils/apiErrors";

export interface ActionFeedbackOptions {
  title?: string;
  code?: string;
}

/**
 * Centered popup for backend/API errors — success/info toasts stay on
 * react-toastify.
 *
 * Returns whether a dialog actually opened: the sanitizer can strip a purely
 * technical message down to nothing, and callers that must not fail silently
 * need to know so they can fall back to a toast.
 */
export function showActionError(message: string, options?: ActionFeedbackOptions): boolean {
  const trimmed = sanitizeApiErrorMessage(message).trim();
  if (!trimmed) return false;

  useUiStore.getState().showActionFeedback({
    tone: "error",
    title: options?.title ?? "Something went wrong",
    message: trimmed,
    code: options?.code,
  });
  return true;
}

/** Centered popup for success on long forms where top toasts/banners are easy to miss. */
export function showActionSuccess(message: string, options?: ActionFeedbackOptions) {
  const trimmed = message.trim();
  if (!trimmed) return;

  useUiStore.getState().showActionFeedback({
    tone: "success",
    title: options?.title ?? "Success",
    message: trimmed,
    code: options?.code,
  });
}

export function scrollFeedbackIntoView(
  element: HTMLElement | null | undefined,
  options?: ScrollIntoViewOptions,
) {
  if (!element) return;
  requestAnimationFrame(() => {
    element.scrollIntoView({
      behavior: "smooth",
      block: "center",
      ...options,
    });
  });
}

export function showActionErrorFromUnknown(
  err: unknown,
  fallback = "Something went wrong. Please try again.",
  options?: Omit<ActionFeedbackOptions, "code"> & { code?: string },
) {
  const apiErr = err as { code?: string; message?: string } | null;
  showActionError(getApiErrorMessage(err) ?? fallback, {
    ...options,
    code: options?.code ?? apiErr?.code,
  });
}
